import { memo, useCallback, useMemo, useState } from 'react';

import { eachHourOfInterval, endOfDay, startOfDay, isSameHour } from 'date-fns';

import { getHoursIntervals } from '@calendar/calendarHelpers';
import Hour from '@calendar/components//Hour';
import DayDateTextation from '@calendar/components/DayDateTextation';
import SelectedTimeslot from '@calendar/components/SelectedTimeslots';
import useTimetablesStore from '@calendar/store/useTimetablesStore';
import { isWithinIntervalHelper } from '@calendar/timetableHelpers';
import useHoveredTimeSlots from '@event/hooks/useHoveredTimeSlots';

interface DayProps {
  date: Date;
}

const Day = ({ date }: DayProps) => {
  const { timetables } = useTimetablesStore();
  const [isSelectedTimeSlotModalOpen, setIsSelectedTimeSlotModalOpen] =
    useState<boolean | string>(false);

  const {
    handleMouseEnter,
    handleMouseLeave,
    isHovered,
    middleOfHoveredTimeslot,
  } = useHoveredTimeSlots();

  const hours = useMemo(
    () =>
      eachHourOfInterval({
        start: startOfDay(date),
        end: endOfDay(date),
      }),
    [date],
  );

  const hoursWithSelected = useMemo(
    () =>
      hours.map((hour) => ({
        hour,
        isSelected: timetables?.some((timetable) =>
          isWithinIntervalHelper(hour, timetable),
        ),
        timetable: timetables?.find((timetable) =>
          isWithinIntervalHelper(hour, timetable),
        ),
      })),
    [timetables, hours],
  );

  const hoursIntervals = useMemo(
    () => getHoursIntervals(hoursWithSelected),
    [hoursWithSelected],
  );

  const isDividedByTwo = useMemo(
    () => middleOfHoveredTimeslot?.isDividedByTwo || false,
    [middleOfHoveredTimeslot],
  );

  const isInMiddleOfSelectedInterval = useCallback(
    (hour: Date) =>
      middleOfHoveredTimeslot && isSameHour(middleOfHoveredTimeslot.hour, hour),
    [middleOfHoveredTimeslot],
  );

  return (
    <div className="static flex flex-row">
      <div className="left-15 absolute bg-white">
        <DayDateTextation date={date} />
      </div>

      <div className="ml-20 flex flex-row">
        {hoursIntervals.map(({ selected, intervals }) => {
          return selected ? (
            <SelectedTimeslot
              key={intervals[0].hour.getTime()}
              startDate={intervals[0].hour}
              length={intervals.length}
              isSelectedTimeSlotModalOpen={isSelectedTimeSlotModalOpen}
              setIsSelectedTimeSlotModalOpen={setIsSelectedTimeSlotModalOpen}
            />
          ) : (
            intervals.map(({ hour }) => (
              <Hour
                key={hour.getTime()}
                startTimeOfHoveredTimeslot={middleOfHoveredTimeslot?.startHour}
                isInMiddleOfSelectedInterval={isInMiddleOfSelectedInterval(
                  hour,
                )}
                isDividedByTwo={isDividedByTwo}
                date={hour}
                isDayWithSelectedInterval={hoursIntervals.length !== 1}
                isHovered={isHovered(hour)}
                handleMouseEnter={handleMouseEnter}
                handleMouseLeave={handleMouseLeave}
                isSelectedTimeSlotModalOpen={isSelectedTimeSlotModalOpen}
                setIsSelectedTimeSlotModalOpen={setIsSelectedTimeSlotModalOpen}
              />
            ))
          );
        })}
      </div>
    </div>
  );
};

export default memo(
  Day,
  (prevProps, nextProps) => prevProps.date === nextProps.date,
);
